import React, { useCallback, useEffect, useState } from "react";
import { FiX, FiPaperclip, FiFileText, FiMail, FiPlayCircle } from "react-icons/fi";
import style from "../monetization/Monetization.module.css";
import local from "./SupportTickets.module.css";
import { showToast } from "../../utils/Toastify/Toastify";
import {
  getSupportTickets,
  getSupportTicket,
  updateSupportTicket,
  replySupportTicket,
} from "../../api/HttpApis";
import { useRbac } from "../../rbac/RbacContext";

// Creator "Help & Support" submissions. Creators send them from the CMS
// Help & Support page; the support inbox also gets each one by email.
// Creator (CMS) vs viewer (website) request.
const SourceTag = ({ source }) => (
  <span className={`${local.sourceTag} ${source === "viewer" ? local.sourceViewer : ""}`}>
    {source === "viewer" ? "Viewer" : "Creator"}
  </span>
);

const STATUS_TABS = [
  { key: "open", label: "Open" },
  { key: "in_progress", label: "In Progress" },
  { key: "resolved", label: "Resolved" },
  { key: "all", label: "All" },
];
const STATUS_LABELS = { open: "Open", in_progress: "In Progress", resolved: "Resolved" };
const statusChip = (s) =>
  ({
    open: style.chipPending,
    in_progress: style.chipHolding,
    resolved: style.chipPaid,
  }[s] || style.chipHolding);

// Tells the sidebar to refresh its open-ticket badge after a save.
export const SUPPORT_TICKETS_CHANGED = "support-tickets-changed";

const fmtDate = (d) => (d ? new Date(d).toLocaleString() : "—");

// The subject the support inbox received, so a reply threads with it.
const replySubject = (t) =>
  `Re: [${t.source === "viewer" ? "Viewer" : "Creator"} Support #${t.id}] ${t.subject}`;

// Compose box for "Reply via email". The backend sends it from the app's
// sender with Reply-To set to the support inbox.
function ReplyModal({ ticket, onClose, onSent }) {
  // Always the "Re: …" subject, so the reply threads with the ticket mail.
  const subject = replySubject(ticket);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const canSend = subject.trim() && message.trim() && !busy;

  const handleSend = async () => {
    setBusy(true);
    try {
      const res = await replySupportTicket(ticket.id, {
        subject: subject.trim(),
        message: message.trim(),
      });
      showToast(res?.message || "Reply sent", "success");
      onSent(res?.data);
    } catch (err) {
      showToast(err?.response?.data?.message || "Could not send the reply", "error");
      setBusy(false);
    }
  };

  return (
    // Rendered inside the ticket overlay: stop the click so it doesn't close that too.
    <div
      className={style.modalOverlay}
      style={{ zIndex: 1001 }}
      onClick={(e) => {
        e.stopPropagation();
        if (!busy) onClose();
      }}
    >
      <div className={style.modal} style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <h3 className={style.modalTitle}>Reply to {ticket.creator_name || ticket.creator_email}</h3>
        <div className={local.muted} style={{ fontSize: 12, marginBottom: 14 }}>
          To: {ticket.creator_email}
        </div>

        <label className={local.fieldLabel} style={{ display: "block" }} htmlFor="reply-subject">Subject</label>
        <input
          id="reply-subject"
          className={style.input}
          style={{ width: "100%" }}
          value={subject}
          readOnly
        />

        <label className={local.fieldLabel} style={{ display: "block" }} htmlFor="reply-message">Message</label>
        <textarea
          id="reply-message"
          className={style.noteInput}
          style={{ minHeight: 160 }}
          placeholder="Write your reply…"
          value={message}
          maxLength={10000}
          onChange={(e) => setMessage(e.target.value)}
          disabled={busy}
          autoFocus
        />

        <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "flex-end" }}>
          <button className={`${style.btn} ${style.btnGhost} ${style.btnSm}`} onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className={`${style.btn} ${style.btnSm}`} onClick={handleSend} disabled={!canSend}>
            {busy ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailModal({ id, canEdit, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("open");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [replying, setReplying] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await getSupportTicket(id);
        const t = res?.data ?? null;
        if (!alive) return;
        setData(t);
        setStatus(t?.status || "open");
        setNote(t?.admin_note || "");
      } catch (err) {
        showToast(err?.response?.data?.message || "Could not load the ticket", "error");
        onClose();
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [id, onClose]);

  const dirty = data && (status !== data.status || note.trim() !== (data.admin_note || ""));

  const handleSave = async () => {
    setBusy(true);
    try {
      const res = await updateSupportTicket(id, { status, admin_note: note });
      setData(res?.data ?? data);
      showToast(res?.message || "Ticket updated", "success");
      window.dispatchEvent(new Event(SUPPORT_TICKETS_CHANGED));
      onSaved();
    } catch (err) {
      showToast(err?.response?.data?.message || "Could not update the ticket", "error");
    } finally {
      setBusy(false);
    }
  };

  // A reply moves an open ticket to in_progress; follow it unless the admin
  // has already picked a different status in the form.
  const handleReplySent = (updated) => {
    setReplying(false);
    if (updated) {
      if (status === data.status) setStatus(updated.status);
      setData(updated);
    }
    window.dispatchEvent(new Event(SUPPORT_TICKETS_CHANGED));
    onSaved();
  };

  return (
    <div className={style.modalOverlay} onClick={onClose}>
      <div className={style.pdfModal} onClick={(e) => e.stopPropagation()} style={{ overflowY: "auto" }}>
        <button type="button" className={style.pdfModalClose} onClick={onClose} aria-label="Close">
          <FiX />
        </button>
        {loading || !data ? (
          <div className={style.loading}>Loading…</div>
        ) : (
          <>
            <div className={local.detailHead}>
              <span className={local.ticketNo}>#{data.id}</span>
              <span className={`${style.chip} ${statusChip(data.status)}`}>
                {STATUS_LABELS[data.status] || data.status}
              </span>
            </div>
            <h2 className={style.title} style={{ fontSize: 18 }}>{data.subject}</h2>

            <dl className={local.meta}>
              <dt>From</dt>
              <dd>
                <SourceTag source={data.source} />{" "}
                {data.creator_name || "—"}
                {data.creator_email && <span className={local.muted}> · {data.creator_email}</span>}
                {data.source === "viewer" && !data.user_id && (
                  <span className={local.muted}> · not signed in</span>
                )}
              </dd>
              <dt>Category</dt>
              <dd>{data.category_label || data.category}</dd>
              <dt>Sent</dt>
              <dd>{fmtDate(data.created_at)}</dd>
              {data.platform && (
                <>
                  <dt>Device</dt>
                  <dd>{data.platform}</dd>
                </>
              )}
              {data.page && (
                <>
                  <dt>Page</dt>
                  <dd className={local.mono}>{data.page}</dd>
                </>
              )}
              {data.user_agent && (
                <>
                  <dt>Browser</dt>
                  <dd className={local.mono}>{data.user_agent}</dd>
                </>
              )}
            </dl>

            <div className={local.message}>{data.message}</div>

            {Array.isArray(data.attachments) && data.attachments.length > 0 && (
              <div className={local.attachments}>
                <div className={local.attachTitle}>
                  <FiPaperclip /> Attachments ({data.attachments.length})
                </div>
                <div className={local.attachGrid}>
                  {data.attachments.map((a) => (
                    <a
                      key={a.url}
                      href={a.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={local.attachCard}
                      title={a.name}
                    >
                      {String(a.mime_type || "").startsWith("image/") ? (
                        <img src={a.url} alt={a.name} className={local.attachImg} />
                      ) : String(a.mime_type || "").startsWith("video/") ? (
                        // First frame as a thumbnail; the card link opens it to play.
                        <span className={local.attachVideo}>
                          <video src={`${a.url}#t=0.1`} preload="metadata" muted className={local.attachImg} />
                          <FiPlayCircle className={local.attachPlay} />
                        </span>
                      ) : (
                        <span className={local.attachDoc}>
                          <FiFileText />
                        </span>
                      )}
                      <span className={local.attachName}>{a.name}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {Array.isArray(data.replies) && data.replies.length > 0 && (
              <div className={local.replies}>
                <div className={local.attachTitle}>
                  <FiMail /> Replies sent ({data.replies.length})
                </div>
                {data.replies.map((r) => (
                  <div key={r.id} className={local.reply}>
                    <div className={local.replyHead}>
                      <strong>{r.subject}</strong>
                      <span className={local.muted}>
                        {r.sent_by_name || "Admin"} · {fmtDate(r.created_at)}
                      </span>
                    </div>
                    <div className={local.muted} style={{ fontSize: 12 }}>To: {r.to_email}</div>
                    <div className={local.replyBody}>{r.message}</div>
                  </div>
                ))}
              </div>
            )}

            {data.creator_email && canEdit && (
              <button
                type="button"
                className={`${style.btn} ${style.btnSm} ${local.replyBtn}`}
                onClick={() => setReplying(true)}
              >
                Reply via email
              </button>
            )}

            <div className={local.manage}>
              <label className={local.fieldLabel} htmlFor="ticket-status">Status</label>
              <select
                id="ticket-status"
                className={style.select}
                style={{ width: 200 }}
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                disabled={!canEdit || busy}
              >
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
              </select>

              <label className={local.fieldLabel} htmlFor="ticket-note">
                Internal note <span className={local.muted}>(only admins see this)</span>
              </label>
              <textarea
                id="ticket-note"
                className={style.noteInput}
                placeholder="What was done, who is handling it…"
                value={note}
                maxLength={5000}
                onChange={(e) => setNote(e.target.value)}
                disabled={!canEdit || busy}
              />

              {data.updated_by && (
                <div className={local.muted} style={{ fontSize: 12, marginTop: 8 }}>
                  Last updated {fmtDate(data.updated_at)} by {data.updated_by.name || data.updated_by.email}
                  {data.resolved_at ? ` · resolved ${fmtDate(data.resolved_at)}` : ""}
                </div>
              )}

              {canEdit && (
                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                  <button className={`${style.btn} ${style.btnSm}`} onClick={handleSave} disabled={busy || !dirty}>
                    {busy ? "Saving…" : "Save"}
                  </button>
                  <button className={`${style.btn} ${style.btnGhost} ${style.btnSm}`} onClick={onClose} disabled={busy}>
                    Close
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      {replying && data && (
        <ReplyModal ticket={data} onClose={() => setReplying(false)} onSent={handleReplySent} />
      )}
    </div>
  );
}

export default function SupportTickets() {
  const { can } = useRbac();
  const canEdit = can("support", "edit");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({});
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("open");
  const [source, setSource] = useState("");
  const [category, setCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const limit = 20;

  // Debounce the search box so typing doesn't fire a request per key.
  useEffect(() => {
    const t = setTimeout(() => {
      setQuery(search.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getSupportTickets({ page, limit, status, source, category, q: query });
      const payload = res?.data ?? {};
      setRows(Array.isArray(payload.data) ? payload.data : []);
      setTotal(payload.total || 0);
      setCounts(payload.counts || {});
      if (Array.isArray(payload.categories)) setCategories(payload.categories);
    } catch (err) {
      showToast("Failed to load support tickets", "error");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page, status, source, category, query]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const closeDetail = useCallback(() => setDetailId(null), []);
  const lastPage = Math.max(1, Math.ceil(total / limit));

  return (
    <div className={style.page}>
      <div className={style.headerRow}>
        <div>
          <h2 className={style.title}>Support Tickets</h2>
          <p className={style.subtitle}>
            Requests from creators (CMS Help &amp; Support) and viewers (website Contact Support).
            Reply by email, then mark the ticket resolved.
          </p>
        </div>
      </div>

      <div className={style.toolbar}>
        <div className={local.tabs} role="tablist">
          {STATUS_TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={status === t.key}
              className={`${local.tab} ${status === t.key ? local.tabActive : ""}`}
              onClick={() => {
                setStatus(t.key);
                setPage(1);
              }}
            >
              {t.label}
              {counts[t.key] != null && <span className={local.tabCount}>{counts[t.key]}</span>}
            </button>
          ))}
        </div>
        <select
          className={style.select}
          style={{ width: 150 }}
          value={source}
          onChange={(e) => {
            setSource(e.target.value);
            // Category keys differ per source, so a stale one could match nothing.
            setCategory("");
            setPage(1);
          }}
          aria-label="Filter by source"
        >
          <option value="">All sources</option>
          <option value="creator">Creators</option>
          <option value="viewer">Viewers</option>
        </select>
        <select
          className={style.select}
          style={{ width: 240 }}
          value={category}
          onChange={(e) => {
            setCategory(e.target.value);
            setPage(1);
          }}
          aria-label="Filter by category"
        >
          <option value="">All categories</option>
          {categories.some((c) => c.group)
            ? ["Creator", "Viewer"].map((g) => (
                <optgroup key={g} label={`${g} requests`}>
                  {categories
                    .filter((c) => c.group === g)
                    .map((c) => (
                      <option key={`${g}-${c.key}`} value={c.key}>
                        {c.label}
                      </option>
                    ))}
                </optgroup>
              ))
            : categories.map((c) => (
                <option key={c.key} value={c.key}>
                  {c.label}
                </option>
              ))}
        </select>
        <input
          type="search"
          className={style.search}
          style={{ maxWidth: 300 }}
          placeholder="Search tickets or #id"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className={style.tableWrap}>
        <table className={style.table}>
          <thead>
            <tr>
              <th>Ticket</th>
              <th>From</th>
              <th>Category</th>
              <th>Subject</th>
              <th>Status</th>
              <th>Received</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className={style.loading}>Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={7} className={style.empty}>
                  {query || category ? "No tickets match these filters." : "No tickets here."}
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.id}>
                  <td className={local.ticketNo}>#{r.id}</td>
                  <td>
                    <SourceTag source={r.source} /> {r.creator_name || "—"}
                    <div className={local.muted} style={{ fontSize: 11 }}>
                      {r.creator_email}
                      {r.platform ? ` · ${r.platform}` : ""}
                    </div>
                  </td>
                  <td>
                    <span className={local.category}>{r.category_label || r.category}</span>
                  </td>
                  <td style={{ maxWidth: 380 }}>
                    {/* A common-problem ticket's subject IS its category
                        label, so don't print it twice — lead with the message. */}
                    {r.subject === r.category_label ? (
                      <div className={local.subject}>{r.message}</div>
                    ) : (
                      <>
                        <div className={local.subject}>{r.subject}</div>
                        <div className={local.preview}>{r.message}</div>
                      </>
                    )}
                  </td>
                  <td>
                    <span className={`${style.chip} ${statusChip(r.status)}`}>
                      {STATUS_LABELS[r.status] || r.status}
                    </span>
                  </td>
                  <td>{fmtDate(r.created_at)}</td>
                  <td>
                    <button className={`${style.btn} ${style.btnGhost} ${style.btnSm}`} onClick={() => setDetailId(r.id)}>
                      View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {total > limit && (
        <div className={style.toolbar} style={{ marginTop: 16, justifyContent: "flex-end" }}>
          <button className={`${style.btn} ${style.btnGhost} ${style.btnSm}`} disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Prev
          </button>
          <span style={{ alignSelf: "center", fontSize: 13 }}>Page {page} of {lastPage}</span>
          <button className={`${style.btn} ${style.btnGhost} ${style.btnSm}`} disabled={page >= lastPage} onClick={() => setPage((p) => p + 1)}>
            Next
          </button>
        </div>
      )}

      {detailId != null && (
        <DetailModal id={detailId} canEdit={canEdit} onClose={closeDetail} onSaved={fetchRows} />
      )}
    </div>
  );
}
